import {
  CLINIC_CRF_V2,
  createClinicCrfPackage,
  deepClone,
  getAtPath,
  hasAtPath,
  pruneHiddenData,
  setAtPath,
  validatePackage,
  type ClinicCrfCondition,
  type ClinicCrfConditionOperator,
  type ClinicCrfData,
  type ClinicCrfDefinitionV2,
  type ClinicCrfField,
  type ClinicCrfFieldPresentation,
  type ClinicCrfFieldType,
  type ClinicCrfPackageV1,
  type ClinicCrfPresentation,
  type ClinicCrfPackageForm,
  type JsonScalar,
} from '@clinic-crf/contract'

export const MAKER_DRAFT_KEY = 'strefabadan:ecrf-maker:draft:v1'
export const MAX_IMPORT_BYTES = 2 * 1024 * 1024

export function sanitizePreviewAnswers(
  definition: ClinicCrfDefinitionV2,
  input: ClinicCrfData,
): ClinicCrfData {
  const visible = pruneHiddenData(definition, input)
  const output: ClinicCrfData = {}
  for (const field of definition.fields) {
    if (hasAtPath(visible, field.path)) {
      setAtPath(output, field.path, deepClone(getAtPath(visible, field.path)))
    }
  }

  return pruneHiddenData(definition, output)
}

export interface MakerDraft {
  format: 'clinic-ecrf-maker-draft.v1'
  saved_at: string
  form: ClinicCrfPackageForm
  definition: ClinicCrfPackageV1['definition']
  presentation: ClinicCrfPresentation
}

export function moveItem<T>(items: readonly T[], from: number, to: number): T[] {
  const output = [...items]
  if (from < 0 || from >= output.length || to < 0 || to >= output.length || from === to) return output
  const [item] = output.splice(from, 1)
  if (item !== undefined) output.splice(to, 0, item)
  return output
}

export function pathFromLabel(label: string, fallback = 'new_field'): string {
  const normalized = label
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^[^a-z]+/, '')
  return normalized || fallback
}

export function uniqueFieldPath(base: string, fields: readonly ClinicCrfField[]): string {
  const clean = pathFromLabel(base)
  const existing = new Set(fields.map((field) => field.path))
  if (!existing.has(clean)) return clean
  let suffix = 2
  while (existing.has(`${clean}_${suffix}`)) suffix += 1
  return `${clean}_${suffix}`
}

export function uniqueSectionId(base: string, existingIds: readonly string[]): string {
  const clean = pathFromLabel(base, 'section').replaceAll('.', '_').slice(0, 80)
  if (!existingIds.includes(clean)) return clean
  let suffix = 2
  while (existingIds.includes(`${clean}_${suffix}`)) suffix += 1
  return `${clean}_${suffix}`
}

export function createField(type: ClinicCrfFieldType, fields: readonly ClinicCrfField[]): ClinicCrfField {
  const path = uniqueFieldPath(`field_${fields.length + 1}`, fields)
  const field: ClinicCrfField = {
    path,
    label: 'Nowe pole',
    type,
    source: 'site',
  }
  if (type === 'choice' || type === 'multichoice') field.options = ['option_1', 'option_2']
  if (type === 'text') field.max_length = 4000
  if (type === 'string') field.max_length = 255
  if (type === 'number') field.precision = 1
  return field
}

export function normalizeFieldForType(field: ClinicCrfField, type: ClinicCrfFieldType): ClinicCrfField {
  const next = deepClone(field)
  next.type = type
  if (!['choice', 'multichoice'].includes(type)) delete next.options
  else if (!next.options?.length) next.options = ['option_1', 'option_2']
  if (!['integer', 'number'].includes(type)) {
    delete next.min
    delete next.max
    delete next.precision
    delete next.unit
  }
  if (type === 'integer') delete next.precision
  if (!['string', 'text'].includes(type)) {
    delete next.min_length
    delete next.max_length
  }
  if (type === 'partial_date') {
    delete next.min
    delete next.max
    delete next.precision
    delete next.options
    delete next.min_length
    delete next.max_length
  }
  return next
}

export function normalizePresentationForFieldType(
  presentation: ClinicCrfFieldPresentation,
  type: ClinicCrfFieldType,
): ClinicCrfFieldPresentation {
  const next = deepClone(presentation)
  if (!['choice', 'multichoice'].includes(type)) delete next.option_labels
  return next
}

function conditionScalarFromText(raw: string, fieldType?: ClinicCrfFieldType): JsonScalar {
  if (['choice', 'multichoice', 'string', 'text', 'date', 'datetime', 'partial_date'].includes(fieldType ?? '')) {
    return raw
  }
  if (fieldType === 'boolean') {
    if (raw === 'true') return true
    if (raw === 'false') return false
    if (raw === 'null') return null
    return raw
  }
  if (fieldType === 'integer' || fieldType === 'number') {
    if (raw === 'null') return null
    const number = Number(raw)
    return raw.trim() !== '' && Number.isFinite(number) ? number : raw
  }

  if (raw === 'true') return true
  if (raw === 'false') return false
  if (raw === 'null') return null
  const number = Number(raw)
  return raw.trim() !== '' && Number.isFinite(number) ? number : raw
}

export function conditionOperandFromText(
  raw: string,
  operator: ClinicCrfConditionOperator,
  fieldType?: ClinicCrfFieldType,
): JsonScalar | JsonScalar[] {
  if (operator === 'in' || operator === 'not_in') {
    return raw
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
      .map((value) => conditionScalarFromText(value, fieldType))
  }
  return conditionScalarFromText(raw, fieldType)
}

function replaceConditionPath(condition: ClinicCrfCondition | undefined, oldPath: string, newPath: string): void {
  if (!condition) return
  if ('all' in condition) condition.all.forEach((child) => replaceConditionPath(child, oldPath, newPath))
  else if ('any' in condition) condition.any.forEach((child) => replaceConditionPath(child, oldPath, newPath))
  else if ('not' in condition) replaceConditionPath(condition.not, oldPath, newPath)
  else {
    if (condition.field === oldPath) condition.field = newPath
    if (condition.compare_field === oldPath) condition.compare_field = newPath
  }
}

export function renameFieldPath(packageValue: ClinicCrfPackageV1, oldPath: string, newPath: string): void {
  if (oldPath === newPath) return
  const field = packageValue.definition.fields.find((candidate) => candidate.path === oldPath)
  if (!field) return
  field.path = newPath
  packageValue.presentation?.sections.forEach((section) => {
    section.field_paths = section.field_paths.map((path) => (path === oldPath ? newPath : path))
  })
  if (packageValue.presentation?.fields?.[oldPath]) {
    packageValue.presentation.fields[newPath] = packageValue.presentation.fields[oldPath]
    delete packageValue.presentation.fields[oldPath]
  }
  packageValue.definition.fields.forEach((candidate) => {
    replaceConditionPath(candidate.visible_when, oldPath, newPath)
    replaceConditionPath(candidate.required_when, oldPath, newPath)
  })
  packageValue.definition.rules?.forEach((rule) => {
    if (rule.path === oldPath) rule.path = newPath
    replaceConditionPath(rule.when, oldPath, newPath)
    replaceConditionPath(rule.assert, oldPath, newPath)
  })
}

export function removeField(packageValue: ClinicCrfPackageV1, path: string): void {
  if (packageValue.definition.fields.length <= 1) return
  packageValue.definition.fields = packageValue.definition.fields.filter((field) => field.path !== path)
  packageValue.presentation?.sections.forEach((section) => {
    section.field_paths = section.field_paths.filter((candidate) => candidate !== path)
  })
  if (packageValue.presentation?.fields) delete packageValue.presentation.fields[path]
  packageValue.definition.fields.forEach((field) => {
    if (conditionReferences(field.visible_when, path)) delete field.visible_when
    if (conditionReferences(field.required_when, path)) delete field.required_when
  })
  packageValue.definition.rules = (packageValue.definition.rules ?? []).filter(
    (rule) => rule.path !== path && !conditionReferences(rule.assert, path) && !conditionReferences(rule.when, path),
  )
}

export function conditionReferences(condition: ClinicCrfCondition | undefined, path: string): boolean {
  if (!condition) return false
  if ('all' in condition) return condition.all.some((child) => conditionReferences(child, path))
  if ('any' in condition) return condition.any.some((child) => conditionReferences(child, path))
  if ('not' in condition) return conditionReferences(condition.not, path)
  return condition.field === path || condition.compare_field === path
}

export function duplicateField(packageValue: ClinicCrfPackageV1, sourcePath: string): string | null {
  const index = packageValue.definition.fields.findIndex((field) => field.path === sourcePath)
  if (index < 0 || packageValue.definition.fields.length >= 500) return null
  const source = packageValue.definition.fields[index]
  if (!source) return null
  const duplicate = deepClone(source)
  duplicate.path = uniqueFieldPath(`${source.path}_copy`, packageValue.definition.fields)
  duplicate.label = `${source.label} — kopia`
  delete duplicate.visible_when
  delete duplicate.required_when
  packageValue.definition.fields.splice(index + 1, 0, duplicate)
  const section = packageValue.presentation?.sections.find((candidate) => candidate.field_paths.includes(sourcePath))
  if (section) {
    const sourceIndex = section.field_paths.indexOf(sourcePath)
    section.field_paths.splice(sourceIndex + 1, 0, duplicate.path)
  }
  const sourcePresentation = packageValue.presentation?.fields?.[sourcePath]
  if (sourcePresentation) {
    packageValue.presentation ??= { default_locale: 'pl', sections: [] }
    packageValue.presentation.fields ??= {}
    packageValue.presentation.fields[duplicate.path] = deepClone(sourcePresentation)
  }
  return duplicate.path
}

export function toMakerDraft(packageValue: ClinicCrfPackageV1, now = new Date()): MakerDraft {
  return {
    format: 'clinic-ecrf-maker-draft.v1',
    saved_at: now.toISOString(),
    form: deepClone(packageValue.form),
    definition: deepClone(packageValue.definition),
    presentation: deepClone(packageValue.presentation ?? { default_locale: 'pl', sections: [] }),
  }
}

export function packageFromMakerDraft(value: unknown): ClinicCrfPackageV1 | null {
  if (!value || typeof value !== 'object') return null
  const draft = value as Partial<MakerDraft>
  if (draft.format !== 'clinic-ecrf-maker-draft.v1' || !draft.form || !draft.definition || !draft.presentation) return null
  const candidate = createClinicCrfPackage(
    deepClone(draft.definition),
    deepClone(draft.form),
    deepClone(draft.presentation),
  )
  const result = validatePackage(candidate)
  return result.value
}

export function readDraft(storage: Pick<Storage, 'getItem'>): { package: ClinicCrfPackageV1; savedAt: string } | null {
  const raw = storage.getItem(MAKER_DRAFT_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as MakerDraft
    const packageValue = packageFromMakerDraft(parsed)
    return packageValue ? { package: packageValue, savedAt: parsed.saved_at } : null
  } catch {
    return null
  }
}

export function writeDraft(storage: Pick<Storage, 'setItem'>, packageValue: ClinicCrfPackageV1, now = new Date()): string {
  const draft = toMakerDraft(packageValue, now)
  storage.setItem(MAKER_DRAFT_KEY, JSON.stringify(draft))
  return draft.saved_at
}

export class SnapshotHistory<T> {
  private snapshots: string[] = []
  private pointer = -1

  constructor(initial: T, private readonly limit = 60) {
    this.push(initial)
  }

  push(value: T): void {
    const serialized = JSON.stringify(value)
    if (this.snapshots[this.pointer] === serialized) return
    this.snapshots = this.snapshots.slice(0, this.pointer + 1)
    this.snapshots.push(serialized)
    if (this.snapshots.length > this.limit) this.snapshots.shift()
    this.pointer = this.snapshots.length - 1
  }

  undo(): T | null {
    if (!this.canUndo) return null
    this.pointer -= 1
    return JSON.parse(this.snapshots[this.pointer] as string) as T
  }

  redo(): T | null {
    if (!this.canRedo) return null
    this.pointer += 1
    return JSON.parse(this.snapshots[this.pointer] as string) as T
  }

  get canUndo(): boolean {
    return this.pointer > 0
  }

  get canRedo(): boolean {
    return this.pointer >= 0 && this.pointer < this.snapshots.length - 1
  }
}

export function blankPackage(): ClinicCrfPackageV1 {
  return createClinicCrfPackage(
    {
      version: CLINIC_CRF_V2,
      fields: [createField('string', [])],
      rules: [],
    },
    { code: 'NEW_FORM', name: 'Nowy formularz kliniczny', version: 1 },
    {
      default_locale: 'pl',
      sections: [{ id: 'form', title: 'Formularz', field_paths: ['field_1'] }],
      fields: {},
    },
  )
}
